<script lang="ts">
  import DOMPurify from "dompurify";
  import { marked } from "marked";

  import {
    findQuoteAlternatives,
    findSegment,
    splitOnEllipsis,
    type AlternativeMatch,
    type MatchRange,
  } from "../../api/quoteMatch";
  import { app } from "../../state/app.svelte";
  import { sourceMeta } from "../../api/citations";
  import { i18n } from "../../i18n/i18n.svelte";

  const fr = {
    close: "Fermer",
    title: (n: number) => `Passage${n > 1 ? "s" : ""} source${n > 1 ? ` (${n})` : ""}`,
    searchAria: "Rechercher (Ctrl+F)",
    searchTitle: "Rechercher dans le passage (Ctrl+F)",
    searchPlaceholder: "Rechercher dans le passage…",
    prevTitle: "Précédent (Maj+Entrée)",
    prev: "Précédent",
    nextTitle: "Suivant (Entrée)",
    next: "Suivant",
    closeSearchTitle: "Fermer (Échap)",
    closeSearch: "Fermer la recherche",
    keyPhrase: "Phrase clé :",
    quoted: (s: string) => `« ${s} »`,
    notFoundSeeBelow: "Correspondance non trouvée dans le passage cité — voir ci-dessous.",
    notFound: "Correspondance non trouvée dans le passage.",
    cosine: "Similarité cosinus",
    foundElsewhere: "Phrase introuvable dans le passage cité — trouvée dans :",
    possibleSource: "Source possible",
    segmentsTitle: "Segments du quote retrouvés dans ce passage",
    openSource: (page: number) => `Ouvrir la source${page > 0 ? ` (page ${page})` : ""}`,
  };
  const en: typeof fr = {
    close: "Close",
    title: (n) => `Source passage${n > 1 ? `s (${n})` : ""}`,
    searchAria: "Search (Ctrl+F)",
    searchTitle: "Search in the passage (Ctrl+F)",
    searchPlaceholder: "Search in the passage…",
    prevTitle: "Previous (Shift+Enter)",
    prev: "Previous",
    nextTitle: "Next (Enter)",
    next: "Next",
    closeSearchTitle: "Close (Esc)",
    closeSearch: "Close search",
    keyPhrase: "Key phrase:",
    quoted: (s) => `“${s}”`,
    notFoundSeeBelow: "No match found in the cited passage — see below.",
    notFound: "No match found in the passage.",
    cosine: "Cosine similarity",
    foundElsewhere: "Phrase not found in the cited passage — found in:",
    possibleSource: "Possible source",
    segmentsTitle: "Quote segments found in this passage",
    openSource: (page) => `Open the source${page > 0 ? ` (page ${page})` : ""}`,
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  function renderChunk(text: string): string {
    return DOMPurify.sanitize(
      marked.parse(text, { async: false, gfm: true, breaks: true }) as string,
    );
  }

  // ── In-modal find (Ctrl+F / Cmd+F) ─────────────────────────────────────
  // The browser's native find-bar targets the underlying tab, not the side
  // panel, so we ship our own. Lives entirely inside this modal.

  let searchOpen = $state(false);
  let searchQuery = $state("");
  let searchHits = $state<HTMLSpanElement[]>([]);
  let activeHitIdx = $state(-1);
  let searchInputEl = $state<HTMLInputElement | undefined>(undefined);
  let bodyEl = $state<HTMLElement | undefined>(undefined);

  function clearSearchHits() {
    for (const hit of searchHits) {
      const parent = hit.parentNode;
      if (!parent) continue;
      while (hit.firstChild) parent.insertBefore(hit.firstChild, hit);
      parent.removeChild(hit);
      parent.normalize?.();
    }
    searchHits = [];
    activeHitIdx = -1;
  }

  // PDF parsers (PyMuPDF, Docling…) often emit U+00A0 (nbsp), U+202F (narrow
  // nbsp) or U+2007 (figure space) instead of ASCII spaces — they look identical
  // on screen but break literal indexOf. Substitute them 1-for-1 so the match
  // length stays equal to the user's typed query length and splitText offsets
  // remain valid.
  const SOFT_WS_RE = /[   ]/g;
  function normalizeForSearch(s: string): string {
    return s.replace(SOFT_WS_RE, " ").toLowerCase();
  }

  function runSearch(query: string) {
    clearSearchHits();
    const q = query.trim();
    if (!q || !bodyEl) return;
    const needle = normalizeForSearch(q);

    // Snapshot text nodes BEFORE mutating the DOM, so splits don't perturb
    // the iteration. We also skip anything already inside a search-hit span
    // (defensive — clearSearchHits should have removed them all).
    const walker = document.createTreeWalker(bodyEl, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let n: Node | null;
    while ((n = walker.nextNode())) {
      const t = n as Text;
      if ((t.parentElement?.classList.contains("search-hit") ?? false)) continue;
      if (!t.textContent) continue;
      textNodes.push(t);
    }

    const newHits: HTMLSpanElement[] = [];
    for (const node of textNodes) {
      let remaining: Text = node;
      while (true) {
        const text = remaining.textContent ?? "";
        const idx = normalizeForSearch(text).indexOf(needle);
        if (idx < 0) break;
        const matchNode = remaining.splitText(idx);
        const afterNode = matchNode.splitText(q.length);
        const span = document.createElement("span");
        span.className = "search-hit";
        matchNode.parentNode?.insertBefore(span, matchNode);
        span.appendChild(matchNode);
        newHits.push(span);
        remaining = afterNode;
      }
    }

    searchHits = newHits;
    activeHitIdx = newHits.length > 0 ? 0 : -1;
    if (activeHitIdx >= 0) focusHit(0);
  }

  function focusHit(idx: number) {
    for (const h of searchHits) h.classList.remove("active");
    const hit = searchHits[idx];
    if (!hit) return;
    hit.classList.add("active");
    hit.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  function nextHit() {
    if (searchHits.length === 0) return;
    activeHitIdx = (activeHitIdx + 1) % searchHits.length;
    focusHit(activeHitIdx);
  }

  function prevHit() {
    if (searchHits.length === 0) return;
    activeHitIdx = (activeHitIdx - 1 + searchHits.length) % searchHits.length;
    focusHit(activeHitIdx);
  }

  function openSearch() {
    searchOpen = true;
    queueMicrotask(() => {
      searchInputEl?.focus();
      searchInputEl?.select();
    });
  }

  function closeSearch() {
    searchOpen = false;
    clearSearchHits();
    searchQuery = "";
  }

  function onSearchInput(e: Event) {
    searchQuery = (e.currentTarget as HTMLInputElement).value;
    runSearch(searchQuery);
  }

  // Reset search state any time the citation preview changes (open/close/switch),
  // so an old search doesn't survive into a new passage.
  $effect(() => {
    app.citationPreview;
    searchOpen = false;
    searchQuery = "";
    searchHits = [];
    activeHitIdx = -1;
  });

  function onKeydown(e: KeyboardEvent) {
    if (!app.citationPreview) return;

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
      e.preventDefault();
      openSearch();
      return;
    }

    if (e.key === "Escape") {
      if (searchOpen) closeSearch();
      else app.closeCitation();
      return;
    }

    if (searchOpen && e.key === "Enter") {
      e.preventDefault();
      if (e.shiftKey) prevHit();
      else nextHit();
    }
  }

  interface QuoteMatchSummary {
    exact: number;
    fuzzy: number;
    missing: number;
    total: number;
  }

  /** Highlight every segment of `quote` (split on ellipsis) inside `container`.
   *  Returns counts by match quality. */
  function highlightQuote(
    container: HTMLElement,
    quote: string,
  ): QuoteMatchSummary {
    const segments = splitOnEllipsis(quote);
    const total = segments.length || 1;
    if (segments.length === 0)
      return { exact: 0, fuzzy: 0, missing: 1, total: 1 };

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const parts: { node: Text; start: number; length: number }[] = [];
    let concat = "";
    let current: Node | null;
    while ((current = walker.nextNode())) {
      const t = current as Text;
      const text = t.textContent ?? "";
      // Skip whitespace-only text nodes (typically newlines marked inserts
      // between <tr>/<td> in tables). Wrapping such a node in <mark> would
      // place a <mark> as a direct child of <tr>/<tbody>, which the browser
      // then treats as anonymous cell content and the row layout breaks.
      if (!text.trim()) continue;
      parts.push({ node: t, start: concat.length, length: text.length });
      concat += text;
    }

    const matches: MatchRange[] = [];
    let exact = 0;
    let fuzzy = 0;
    for (const seg of segments) {
      const ranges = findSegment(concat, seg);
      if (ranges.length === 0) continue;
      matches.push(...ranges);
      const allExact = ranges.every((r) => r.quality === "exact");
      if (allExact) exact++;
      else fuzzy++;
    }
    const missing = total - exact - fuzzy;
    if (matches.length === 0) return { exact: 0, fuzzy: 0, missing, total };

    // Merge overlaps (preserve worst quality to be honest about fidelity)
    matches.sort((a, b) => a.start - b.start);
    const merged: MatchRange[] = [];
    for (const m of matches) {
      const last = merged[merged.length - 1];
      if (last && m.start <= last.end) {
        last.end = Math.max(last.end, m.end);
        if (m.quality === "fuzzy") last.quality = "fuzzy";
      } else {
        merged.push({ ...m });
      }
    }

    // Apply wraps in reverse so earlier positions stay valid after DOM splits.
    // For each merged range, slice into per-text-node sub-ranges: surroundContents
    // throws on element boundaries (e.g. a quote spanning multiple <td> cells),
    // so we can't wrap a multi-element range as one node — wrap each text node
    // slice individually instead.
    let firstMark: HTMLElement | null = null;
    for (let i = merged.length - 1; i >= 0; i--) {
      const { start, end, quality } = merged[i];
      const sliceParts = parts.filter(
        (p) => p.start < end && p.start + p.length > start,
      );
      // reverse within the range too, so splits don't shift later offsets
      for (let j = sliceParts.length - 1; j >= 0; j--) {
        const p = sliceParts[j];
        const localStart = Math.max(0, start - p.start);
        const localEnd = Math.min(p.length, end - p.start);
        if (localEnd <= localStart) continue;
        try {
          const range = document.createRange();
          range.setStart(p.node, localStart);
          range.setEnd(p.node, localEnd);
          const mark = document.createElement("mark");
          mark.className = quality === "fuzzy" ? "highlight fuzzy" : "highlight";
          range.surroundContents(mark);
          firstMark = mark;
        } catch {
          // text node detached or already wrapped; skip this slice
        }
      }
    }

    if (firstMark) {
      firstMark.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    return { exact, fuzzy, missing, total };
  }

  let matchResult = $state<QuoteMatchSummary | null>(null);
  let alternatives = $state<AlternativeMatch[]>([]);

  // External "Ouvrir la source" links shown in the sticky footer (always
  // visible, no scrolling). Built from every displayed source that carries a
  // sourceUrl — primary refs plus any fallback alternatives — deduped by URL.
  const footerLinks = $derived.by(() => {
    const preview = app.citationPreview;
    if (!preview) return [];
    const refs = [...preview.refs, ...alternatives.map((a) => a.ref)];
    const seen = new Set<string>();
    const out: { url: string; page: number }[] = [];
    for (const r of refs) {
      if (!r.sourceUrl || seen.has(r.sourceUrl)) continue;
      seen.add(r.sourceUrl);
      out.push({ url: r.sourceUrl, page: r.page });
    }
    return out;
  });

  function bodyAction(node: HTMLElement) {
    $effect(() => {
      const preview = app.citationPreview;
      if (!preview || !preview.quote) {
        matchResult = null;
        alternatives = [];
        return;
      }
      queueMicrotask(() => {
        const result = highlightQuote(node, preview.quote!);
        matchResult = result;
        // Fallback search across the rest of the corpus only when the cited
        // chunk(s) had zero matches — likely a chunk-number confusion by the LLM.
        if (result.exact + result.fuzzy === 0 && preview.corpus) {
          const cited = new Set(preview.refs.map((r) => r.number));
          alternatives = findQuoteAlternatives(
            preview.quote!,
            cited,
            preview.corpus,
          );
        } else {
          alternatives = [];
        }
      });
    });
    return {};
  }

  /** Per-alternative-card highlight action: surlignage du quote dans le markdown
   *  d'un chunk alternatif. Indépendant de bodyAction qui ne walk que les chunks
   *  primaires (sinon matchResult.exact deviendrait > 0 et la bannière warning
   *  ne se déclencherait pas). */
  function altHighlightAction(node: HTMLElement) {
    $effect(() => {
      const preview = app.citationPreview;
      if (!preview || !preview.quote) return;
      const quote = preview.quote;
      queueMicrotask(() => {
        highlightQuote(node, quote);
      });
    });
    return {};
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if app.citationPreview}
  {@const preview = app.citationPreview}
  <div
    class="backdrop"
    onclick={() => app.closeCitation()}
    onkeydown={() => {}}
    role="button"
    tabindex="-1"
    aria-label={t.close}
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="citation-title"
      onclick={(e) => e.stopPropagation()}
      onkeydown={() => {}}
      tabindex="-1"
    >
      <header class="head">
        <h2 id="citation-title">
          {t.title(preview.refs.length)}
        </h2>
        <button
          class="head-btn"
          onclick={() => (searchOpen ? closeSearch() : openSearch())}
          aria-label={t.searchAria}
          title={t.searchTitle}
        >⌕</button>
        <button class="close" onclick={() => app.closeCitation()} aria-label={t.close}>×</button>
      </header>

      {#if searchOpen}
        <div class="search-bar">
          <input
            bind:this={searchInputEl}
            value={searchQuery}
            oninput={onSearchInput}
            type="text"
            placeholder={t.searchPlaceholder}
            spellcheck="false"
            autocomplete="off"
          />
          <span class="counter" class:none={searchHits.length === 0 && searchQuery}>
            {#if searchHits.length === 0 && searchQuery}
              0
            {:else if searchHits.length > 0}
              {activeHitIdx + 1}/{searchHits.length}
            {:else}
              &nbsp;
            {/if}
          </span>
          <button
            class="nav"
            onclick={prevHit}
            disabled={searchHits.length < 2}
            title={t.prevTitle}
            aria-label={t.prev}
          >↑</button>
          <button
            class="nav"
            onclick={nextHit}
            disabled={searchHits.length < 2}
            title={t.nextTitle}
            aria-label={t.next}
          >↓</button>
          <button
            class="nav"
            onclick={closeSearch}
            title={t.closeSearchTitle}
            aria-label={t.closeSearch}
          >×</button>
        </div>
      {/if}

      {#if preview.quote}
        {@const r = matchResult}
        {@const allMissing = r !== null && r.exact + r.fuzzy === 0}
        {@const hasAlternatives = allMissing && alternatives.length > 0}
        <div
          class="quote-banner"
          class:missing={allMissing && !hasAlternatives}
          class:warning={hasAlternatives}
        >
          <span class="label">{t.keyPhrase}</span>
          <span class="quote">{t.quoted(preview.quote)}</span>
          {#if hasAlternatives}
            <span class="note">
              {t.notFoundSeeBelow}
            </span>
          {:else if allMissing}
            <span class="note">{t.notFound}</span>
          {/if}
        </div>
      {/if}

      <div class="body" bind:this={bodyEl}>
        <div class="primary-chunks" use:bodyAction>
          {#each preview.refs as ref (ref.number)}
            {@const isLocal = !ref.source || ref.source === "local"}
            {@const sm = sourceMeta(ref.source)}
            <article class="chunk">
              <div class="meta">
                {#if !isLocal}
                  <span class="source-badge">{sm.icon} {sm.label}</span>
                {/if}
                <span class="doc-name">{ref.docName}</span>
                {#if isLocal || ref.page > 0}
                  <span class="dot">·</span>
                  <span>p. {ref.page}</span>
                {/if}
                {#if ref.section}
                  <span class="dot">·</span>
                  <span class="section">{ref.section}</span>
                {/if}
                {#if isLocal}
                  <span class="score-chip" title={t.cosine}>
                    {(ref.score * 100).toFixed(0)}%
                  </span>
                {/if}
              </div>
              <div class="markdown">{@html renderChunk(ref.text)}</div>
            </article>
          {/each}
        </div>

        {#if alternatives.length > 0}
          <div class="alt-divider">
            <span class="alt-icon">⚠</span>
            {t.foundElsewhere}
          </div>
          {#each alternatives as alt (alt.ref.number)}
            {@const altLocal = !alt.ref.source || alt.ref.source === "local"}
            {@const altSm = sourceMeta(alt.ref.source)}
            <article class="chunk alt">
              <div class="meta">
                <span class="alt-badge">{t.possibleSource}</span>
                {#if !altLocal}
                  <span class="source-badge">{altSm.icon} {altSm.label}</span>
                {/if}
                <span class="doc-name">{alt.ref.docName}</span>
                {#if altLocal || alt.ref.page > 0}
                  <span class="dot">·</span>
                  <span>p. {alt.ref.page}</span>
                {/if}
                {#if alt.ref.section}
                  <span class="dot">·</span>
                  <span class="section">{alt.ref.section}</span>
                {/if}
                <span class="alt-match-chip" title={t.segmentsTitle}>
                  {alt.segmentsMatched}/{alt.segmentsTotal} segment{alt.segmentsTotal > 1 ? "s" : ""}
                </span>
                {#if altLocal}
                  <span class="score-chip" title={t.cosine}>
                    {(alt.ref.score * 100).toFixed(0)}%
                  </span>
                {/if}
              </div>
              <div class="markdown" use:altHighlightAction>{@html renderChunk(alt.ref.text)}</div>
            </article>
          {/each}
        {/if}
      </div>

      {#if footerLinks.length > 0}
        <div class="modal-footer">
          {#each footerLinks as link (link.url)}
            <a
              class="source-open-btn"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.openSource(link.page)}
            </a>
          {/each}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    padding: 0.75rem;
    animation: fade 0.12s ease-out;
  }

  .modal {
    background: var(--epfl-white);
    border-radius: var(--epfl-radius-lg);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
    max-width: 100%;
    max-height: 90vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: slide 0.15s ease-out;
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.7rem 0.9rem;
    border-bottom: 1px solid var(--epfl-gray-100);
    background: var(--epfl-gray-50);
  }

  h2 {
    margin: 0;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--epfl-text);
  }

  .close {
    background: none;
    border: none;
    font-size: 1.4rem;
    line-height: 1;
    color: var(--epfl-gray-400);
    cursor: pointer;
    padding: 0 0.3rem;
  }

  .close:hover {
    color: var(--epfl-text);
  }

  .head-btn {
    background: none;
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    padding: 0.15rem 0.45rem;
    margin-right: 0.2rem;
    font-size: 0.95rem;
    line-height: 1;
    color: var(--epfl-gray-600);
    cursor: pointer;
    font-family: inherit;
  }

  .head-btn:hover {
    background: var(--epfl-gray-100);
    color: var(--epfl-text);
  }

  .search-bar {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.45rem 0.9rem;
    background: var(--epfl-gray-50);
    border-bottom: 1px solid var(--epfl-gray-100);
  }

  .search-bar input {
    flex: 1;
    padding: 0.3rem 0.5rem;
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    font-size: 0.8rem;
    font-family: inherit;
    outline: none;
  }

  .search-bar input:focus {
    border-color: var(--epfl-accent);
    box-shadow: 0 0 0 1px var(--epfl-accent);
  }

  .search-bar .counter {
    font-size: 0.72rem;
    color: var(--epfl-gray-600);
    min-width: 2.5rem;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  .search-bar .counter.none {
    color: var(--epfl-danger-text);
  }

  .search-bar .nav {
    background: var(--epfl-white);
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    padding: 0.15rem 0.45rem;
    font-size: 0.8rem;
    line-height: 1;
    color: var(--epfl-gray-600);
    cursor: pointer;
    font-family: inherit;
    min-width: 1.6rem;
  }

  .search-bar .nav:hover:not(:disabled) {
    background: var(--epfl-gray-100);
    color: var(--epfl-text);
  }

  .search-bar .nav:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .quote-banner {
    padding: 0.5rem 0.9rem;
    background: var(--epfl-warning-bg);
    border-bottom: 1px solid var(--epfl-warning-border);
    font-size: 0.8rem;
    color: var(--epfl-warning-text);
    line-height: 1.4;
  }

  .quote-banner.missing {
    background: var(--epfl-danger-bg);
    border-bottom-color: var(--epfl-danger-border);
    color: var(--epfl-danger-text);
  }

  .quote-banner.warning {
    background: var(--epfl-warning-border);
    border-bottom-color: var(--epfl-warning-border);
    color: var(--epfl-warning-text);
  }

  .quote-banner .label {
    font-weight: 600;
    margin-right: 0.3rem;
  }

  .quote-banner .quote {
    font-style: italic;
  }

  .quote-banner .note {
    display: block;
    margin-top: 0.2rem;
    font-size: 0.72rem;
    font-style: normal;
    font-weight: 500;
  }

  .body {
    padding: 0.75rem 0.9rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .primary-chunks {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .chunk {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .chunk.alt {
    border-left: 3px dashed var(--epfl-warning);
    padding-left: 0.6rem;
  }

  .alt-divider {
    margin-top: 0.4rem;
    padding: 0.4rem 0.5rem;
    background: var(--epfl-warning-bg);
    border: 1px solid var(--epfl-warning-border);
    border-radius: var(--epfl-radius);
    font-size: 0.78rem;
    font-weight: 500;
    color: var(--epfl-warning-text);
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .alt-icon {
    font-size: 0.95rem;
    line-height: 1;
  }

  .alt-badge {
    background: var(--epfl-warning-border);
    color: var(--epfl-warning-text);
    padding: 0.1rem 0.4rem;
    border-radius: var(--epfl-radius-lg);
    font-size: 0.68rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .alt-match-chip {
    background: var(--epfl-gray-50);
    color: var(--epfl-gray-600);
    padding: 0.1rem 0.4rem;
    border-radius: var(--epfl-radius-lg);
    font-size: 0.7rem;
    font-weight: 500;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.75rem;
    color: var(--epfl-gray-600);
    padding-bottom: 0.4rem;
    border-bottom: 1px dashed var(--epfl-gray-100);
  }

  .doc-name {
    font-weight: 600;
    color: var(--epfl-text);
  }

  .dot {
    color: var(--epfl-gray-200);
  }

  .section {
    color: var(--epfl-accent);
    font-style: italic;
  }

  .score-chip {
    margin-left: auto;
    background: var(--epfl-gray-50);
    color: var(--epfl-accent-dark);
    padding: 0.1rem 0.4rem;
    border-radius: var(--epfl-radius-lg);
    font-size: 0.7rem;
    font-weight: 500;
  }

  .source-badge {
    background: var(--epfl-success-bg);
    color: var(--epfl-success-text);
    padding: 0.1rem 0.4rem;
    border-radius: var(--epfl-radius-lg);
    font-size: 0.7rem;
    font-weight: 500;
    white-space: nowrap;
  }

  /* Sticky footer pinned at the bottom of the modal (.modal is a flex column
     with .body taking the scroll), so "Ouvrir la source" stays visible without
     scrolling back up — mirrors the ServiceNow plugin's modal. */
  .modal-footer {
    flex-shrink: 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 0.6rem 0.9rem 0.75rem;
    border-top: 1px solid var(--epfl-gray-100);
    background: var(--epfl-gray-50);
  }

  .source-open-btn {
    color: var(--epfl-accent);
    font-size: 0.8rem;
    font-weight: 500;
    text-decoration: none;
    padding: 0.4rem 0.7rem;
    border: 1px solid var(--epfl-accent);
    border-radius: var(--epfl-radius);
    white-space: nowrap;
    transition: background-color 0.12s ease, color 0.12s ease;
  }

  .source-open-btn:hover {
    background: var(--epfl-accent);
    color: var(--epfl-white);
  }

  .markdown {
    font-size: 0.85rem;
    line-height: 1.5;
    color: var(--epfl-text);
    word-wrap: break-word;
  }

  .markdown :global(p) {
    margin: 0 0 0.5rem;
  }

  .markdown :global(p:last-child) {
    margin-bottom: 0;
  }

  .markdown :global(ul),
  .markdown :global(ol) {
    margin: 0.3rem 0 0.5rem 1.2rem;
    padding: 0;
  }

  .markdown :global(li) {
    margin: 0.15rem 0;
  }

  .markdown :global(h1),
  .markdown :global(h2),
  .markdown :global(h3),
  .markdown :global(h4),
  .markdown :global(h5) {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0.6rem 0 0.3rem;
    color: var(--epfl-text);
  }

  .markdown :global(strong) {
    font-weight: 600;
  }

  .markdown :global(em) {
    font-style: italic;
  }

  .markdown :global(code) {
    background: var(--epfl-gray-50);
    padding: 0.1rem 0.3rem;
    border-radius: var(--epfl-radius);
    font-size: 0.8rem;
    font-family: var(--epfl-font-mono);
  }

  .markdown :global(pre) {
    background: var(--epfl-gray-50);
    padding: 0.5rem;
    border-radius: var(--epfl-radius);
    overflow-x: auto;
    font-size: 0.78rem;
  }

  .markdown :global(table) {
    border-collapse: collapse;
    font-size: 0.78rem;
    margin: 0.3rem 0;
  }

  .markdown :global(th),
  .markdown :global(td) {
    border: 1px solid var(--epfl-gray-100);
    padding: 0.25rem 0.4rem;
    text-align: left;
  }

  .markdown :global(th) {
    background: var(--epfl-gray-50);
    font-weight: 600;
  }

  .markdown :global(blockquote) {
    border-left: 3px solid var(--epfl-gray-100);
    margin: 0.4rem 0;
    padding: 0.2rem 0 0.2rem 0.7rem;
    color: var(--epfl-gray-600);
  }

  .markdown :global(mark.highlight) {
    background: var(--epfl-warning-border);
    padding: 0.05rem 0.15rem;
    border-radius: var(--epfl-radius);
    animation: pulse 1.2s ease-out;
  }

  .markdown :global(.search-hit) {
    background: var(--epfl-mark);
    border-radius: var(--epfl-radius);
  }

  .markdown :global(.search-hit.active) {
    background: var(--epfl-accent);
    color: var(--epfl-white);
    outline: 2px solid var(--epfl-accent);
    outline-offset: 1px;
  }

  @keyframes pulse {
    0% {
      background: var(--epfl-warning);
    }
    100% {
      background: var(--epfl-warning-border);
    }
  }

  @keyframes fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slide {
    from {
      transform: translateY(12px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>
